import { Component, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { MediaPreviewComponent, TypeChipComponent } from '@shared/components';
import { MediaItem } from '@app/store/medias';
import { MediaType } from '@app/store/shared/models';
import { DialogSearchComponent } from '../../shared/dialog-search/dialog-search.component';
import { MediaSelectionService, MediaTarget } from '../media-selection.service';
import { SelectMediaStore } from '@app/store/medias';

@Component({
    selector: 'app-select-media',
    templateUrl: './select-media.component.html',
    styleUrls: ['./select-media.component.css'],
    imports: [DatePipe, TranslateModule, ButtonModule, PaginatorModule, MediaPreviewComponent, TypeChipComponent, DialogSearchComponent],
    providers: [SelectMediaStore],
})
export class SelectMediaComponent {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly selectMediaStore = inject(SelectMediaStore);
    private readonly mediaSelectionService = inject(MediaSelectionService);
    private readonly translateService = inject(TranslateService);

    readonly mediaTypes: MediaType[];
    readonly target: MediaTarget;

    readonly items = this.selectMediaStore.items;
    readonly total = this.selectMediaStore.total;
    readonly loading = this.selectMediaStore.loading;
    readonly limit = 10;
    readonly first = signal(0);
    readonly searchValue = signal('');

    readonly title = computed(() => {
        if (this.mediaTypes.length === 1) {
            const typeLabel = this.translateService.instant(`media.type.${this.mediaTypes[0]}`);
            return this.translateService.instant('dialogs.selectMedia.typeTitle', { type: typeLabel });
        }
        return this.translateService.instant('dialogs.selectMedia.title');
    });

    readonly uploadLabel = computed(() => {
        if (this.mediaTypes.length === 1 && this.mediaTypes[0] === 'text') {
            return this.translateService.instant('dialogs.selectMedia.createNew');
        }
        return this.translateService.instant('dialogs.selectMedia.upload');
    });

    readonly uploadIcon = computed(() => {
        if (this.mediaTypes.length === 1 && this.mediaTypes[0] === 'text') {
            return 'pi pi-plus';
        }
        return 'pi pi-upload';
    });

    readonly searchPlaceholder = this.translateService.instant('dialogs.selectMedia.search');

    constructor() {
        const params = this.route.parent?.snapshot.params ?? {};
        const rawType = <string>(params['mediaType'] ?? 'video');
        this.mediaTypes = <MediaType[]>rawType.split(',').filter(Boolean);
        this.target = <MediaTarget>(params['mediaTarget'] ?? 'mediaId');
        this.loadMedia();
    }

    onSearch(search: string): void {
        this.searchValue.set(search);
        this.first.set(0);
        this.loadMedia();
    }

    loadMedia(page = 1): void {
        const type = this.mediaTypes.length === 1 ? this.mediaTypes[0] : this.mediaTypes;
        this.selectMediaStore.getMedia({ page, limit: this.limit, type, search: this.searchValue() || undefined });
    }

    onPageChange(event: PaginatorState): void {
        const first = event.first ?? 0;
        this.first.set(first);
        const page = Math.floor(first / this.limit) + 1;
        this.loadMedia(page);
    }

    selectMedia(media: MediaItem): void {
        this.mediaSelectionService.select(media, this.target);
        this.close();
    }

    onUpload(): void {
        if (this.mediaTypes.length > 1) {
            this.router.navigate(['select-type'], { relativeTo: this.route });
            return;
        }
        this.router.navigate(['upload'], { relativeTo: this.route });
    }

    close(): void {
        const outlet = this.route.parent?.outlet ?? 'dialog';
        this.router.navigate([{ outlets: { [outlet]: null } }]);
    }
}
